/**
 * Before/after image comparator. The two images never move or resize —
 * only the clip-path of the top ("before") image changes, driven by a
 * --mp-compare-pos custom property. Pointer Events unify mouse/touch/pen;
 * touch-action: none (set in CSS) stops the page from scrolling while
 * dragging, so no manual preventDefault/passive-listener juggling needed.
 */
(function () {
  'use strict';

  function clamp(value, min, max) {
    return Math.max(min, Math.min(max, value));
  }

  function setupComparer(figure) {
    if (figure.__mpCompareSetup) return;
    figure.__mpCompareSetup = true;

    var handle = figure.querySelector('[data-mp-compare-handle]');
    if (!handle) return;

    var labelLeft = handle.getAttribute('data-label-left') || '';
    var labelRight = handle.getAttribute('data-label-right') || '';
    var dragging = false;

    function setPosition(pct) {
      pct = clamp(pct, 0, 100);
      var rounded = Math.round(pct);
      figure.style.setProperty('--mp-compare-pos', pct + '%');
      handle.setAttribute('aria-valuenow', String(rounded));
      handle.setAttribute(
        'aria-valuetext',
        labelLeft + ' ' + rounded + '%, ' + labelRight + ' ' + (100 - rounded) + '%'
      );
    }

    function pctFromClientX(clientX) {
      var rect = figure.getBoundingClientRect();
      if (!rect.width) return 50;
      return ((clientX - rect.left) / rect.width) * 100;
    }

    function startDrag(event) {
      dragging = true;
      setPosition(pctFromClientX(event.clientX));
      if (handle.setPointerCapture) {
        try {
          handle.setPointerCapture(event.pointerId);
        } catch (err) {
          // Pointer capture can fail harmlessly (e.g. already released); ignore.
        }
      }
    }

    function moveDrag(event) {
      if (!dragging) return;
      setPosition(pctFromClientX(event.clientX));
    }

    function endDrag() {
      dragging = false;
    }

    handle.addEventListener('pointerdown', function (event) {
      handle.focus();
      startDrag(event);
    });
    handle.addEventListener('pointermove', moveDrag);
    handle.addEventListener('pointerup', endDrag);
    handle.addEventListener('pointercancel', endDrag);

    // Clicking/tapping anywhere on the track jumps the handle there too.
    figure.addEventListener('pointerdown', function (event) {
      if (event.target === handle || (handle.contains && handle.contains(event.target))) return;
      handle.focus();
      startDrag(event);
    });

    handle.addEventListener('keydown', function (event) {
      var current = parseFloat(figure.style.getPropertyValue('--mp-compare-pos')) || 50;
      var step = event.shiftKey ? 10 : 2;

      if (event.key === 'ArrowLeft') {
        setPosition(current - step);
        event.preventDefault();
      } else if (event.key === 'ArrowRight') {
        setPosition(current + step);
        event.preventDefault();
      } else if (event.key === 'Home') {
        setPosition(0);
        event.preventDefault();
      } else if (event.key === 'End') {
        setPosition(100);
        event.preventDefault();
      }
    });
  }

  function init(root) {
    root.querySelectorAll('[data-mp-compare]').forEach(setupComparer);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () {
      init(document);
    });
  } else {
    init(document);
  }

  document.addEventListener('shopify:section:load', function (event) {
    init(event.target);
  });
})();
